import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import SearchBar from "../../../components/searchbar/SearchBar";
import { deleteEvents, getAllEvents } from "../../../redux/actions/authActions";

const CalendarDatatable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [open, setOpen] = useState(false);

  const events = useSelector((state) => state.auth.allEvents);
  const token = localStorage.getItem("token");
  

  // Fetch events from the server when the component is mounted
  useEffect(() => {
    if (token) {
      dispatch(getAllEvents(token)); // Dispatch the action to fetch events
    }
  }, [dispatch, token]);

 useEffect(() => {
  if (events.length > 0) {
    const formattedRows = events.map((event, idx) => ({
      ...event,
       id: event._id, 
      title: event.name, 
      date: new Date(event.date).toDateString(),
    }));
    setRows(formattedRows);
  }
}, [events]);

  // Open the delete confirmation dialog
  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setOpen(true);
  };

  // Close the delete dialog
  const handleClose = () => {
    setOpen(false);
    setDeleteId(null);
  };

  // Handle the deletion of an event
  const handleDelete = async () => {
    try {
      dispatch(deleteEvents(deleteId)); // Dispatch delete action
      setRows((prevRows) => prevRows.filter((row) => row.id !== deleteId)); // Update table rows
      handleClose();
    } catch (error) {
      console.error("Error deleting event:", error);
      alert("Failed to delete the event. Please try again.");
    }
  };

  // Handle editing an event
  const handleEdit = (id) => {
    navigate(`${id}`); // Navigate to the edit page for the selected event
  };

  // Handle search functionality
  const handleSearch = (searchTerm) => {
    if (!searchTerm) {
      setRows(events);
      return;
    }
    const filteredRows = events.filter((event) =>
      event.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setRows(filteredRows);
  };

  // Define table columns
  const columns = [
    { field: "date", headerName: "Date", width: 150 },
    { field: "title", headerName: "Event Title", width: 200 },
    { field: "description", headerName: "Description", width: 500 },
    {
      field: "manage",
      headerName: "Manage",
      width: 150,
      renderCell: (params) => (
        <div className="flex gap-2">
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.row._id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleOpenDeleteDialog(params.row._id)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <SearchBar
        titleOne="Add Event"
        onSearch={handleSearch}
        addClientPath="/calendar/add-event"
        showEditButton={false}
        showAddButton={true} // The button will be shown
      />
      <div className="w-full p-4 bg-white rounded-lg shadow border border-gray-200">
        <h2 className="text-xl font-semibold mb-4">Calendar Events</h2>
        <div className="h-[500px]">
          <DataGrid
            rows={rows}
            columns={columns}
            pageSizeOptions={[5, 8, 10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 8, page: 0 } } }}
            pagination
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f9fafb",
                fontWeight: "bold",
                fontSize: "0.875rem",
              },
              "& .MuiDataGrid-cell": {
                fontSize: "0.875rem",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f3f4f6",
              },
              border: "none", // Removed internal border for DataGrid
            }}
          />
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this event? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error">
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CalendarDatatable;
